import { CoolDialogsModule } from '@angular-cool/dialogs';
import { CoolHttpModule } from '@angular-cool/http';
import { CoolStorageModule } from '@angular-cool/storage';
import {
  ApplicationConfig,
  ErrorHandler,
  importProvidersFrom,
  inject,
  provideAppInitializer,
  provideZonelessChangeDetection,
} from '@angular/core';
import { provideNativeDateAdapter } from '@angular/material/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { NoPreloading, provideRouter, withPreloading } from '@angular/router';
import { withNgxsStoragePlugin } from '@ngxs/storage-plugin';
import { provideStore } from '@ngxs/store';
import { GlobalCacheConfig } from 'ts-cacheable';
import { MINUTE_IN_MILLISECONDS } from '../../../common/utils/date.utils';
import { environment } from '../environments/environment';

import { routes } from './app.routes';
import { AppState } from './app.state';
import { SubscriptionUpgradeDialogService } from './common/components/subscription-upgrade-dialog/subscription-upgrade-dialog.service';
import { RedirectAfterService } from './common/routing/redirect-after.service';
import { SessionCheckRouteGuardLogic } from './common/routing/route-guards/session-check.guard';
import { RouteNavigationService } from './common/routing/route-navigation.service';
import { AuthenticationService } from './common/services/authentications.service';
import { ChecklistsService } from './common/services/checklists.service';
import { CompaniesService } from './common/services/companies.service';
import { DialogsService } from './common/services/dialogs.service';
import { ElementsService } from './common/services/elements.service';
import { FilesService } from './common/services/files.service';
import { FormsService } from './common/services/forms.service';
import { UnauthenticatedInterceptor } from './common/services/http/unauthenticated.interceptor';
import { ENVIRONMENT, WINDOW } from './common/services/injection-tokens';
import { KnowledgeBaseService } from './common/services/knowledge-base.service';
import { LemonSqueezyService } from './common/services/lemon-squeezy.service';
import { GlobalErrorHandler } from './common/services/logger/global-error-handler';
import { LoggerService } from './common/services/logger/logger.service';
import { SitesService } from './common/services/sites.service';
import { StartupService } from './common/services/startup.service';
import { TrackingEventsService } from './common/services/tracking-events.service';
import { TrackingService } from './common/services/tracking.service';
import { VisitorsService } from './common/services/visitors.service';
import { PersistedAppState, PersistedAppStateName } from './persisted-app.state';

GlobalCacheConfig.maxCacheCount = 1000;
GlobalCacheConfig.maxAge = 5 * MINUTE_IN_MILLISECONDS;
GlobalCacheConfig.promiseImplementation = Promise;

export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),

    { provide: ENVIRONMENT, useValue: environment },
    { provide: WINDOW, useValue: window },
    { provide: ErrorHandler, useClass: environment.logging?.enabled ? GlobalErrorHandler : ErrorHandler },

    provideAppInitializer(() => inject(StartupService).setupAsync()),

    provideNativeDateAdapter(),

    provideRouter(routes, withPreloading(NoPreloading)),
    provideAnimations(),

    provideStore(
      [AppState, PersistedAppState],
      {
        developmentMode: !environment.production,
      },
      withNgxsStoragePlugin({
        keys: [PersistedAppStateName],
      }),
    ),

    importProvidersFrom(
      CoolHttpModule.forRoot(),
      CoolStorageModule.forRoot(),
      CoolDialogsModule.forRoot(),
    ),

    RouteNavigationService,

    SessionCheckRouteGuardLogic,

    RedirectAfterService,
    UnauthenticatedInterceptor,
    LoggerService,
    StartupService,
    AuthenticationService,

    ElementsService,

    DialogsService,

    SubscriptionUpgradeDialogService,

    CompaniesService,

    KnowledgeBaseService,
    SitesService,
    FormsService,
    ChecklistsService,
    VisitorsService,
    TrackingEventsService,

    FilesService,

    TrackingService,

    LemonSqueezyService,
  ],
};
